import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Building2, Users, IndianRupee, TrendingUp, Target, AlertTriangle, Sparkles, Receipt, Globe, Radio, Send, Star
} from 'lucide-react';
import { Link } from 'react-router-dom';
// Phase 124E — frontend-only PNG capture utility for Wall of Champions demos
import DemoCaptureCard from '@/components/superadmin/DemoCaptureCard';
import AddonsDemandTile from '@/components/superadmin/AddonsDemandTile';
// Phase 270 — Adoption-Nudge Console (scans stuck academies + dispatches)
import AdoptionNudgeConsole from '@/pages/super_admin/AdoptionNudgeConsole';
// Phase 270b — Always-on Onboarding Funnel signal tile (auto-loads on mount)
import OnboardingFunnelTile from '@/pages/super_admin/OnboardingFunnelTile';
// Phase 89E.c — Cross-tenant Webhook Health Dashboard tile
import WebhookHealthTile from '@/components/super-admin/WebhookHealthTile';
// Phase 286.b — Cross-tenant Notification Audit Dashboard tile
import NotificationAuditTile from '@/components/super-admin/NotificationAuditTile';
// Phase 326l — Cross-tenant Activation Funnel tile
import ActivationFunnelCard from '@/components/super-admin/ActivationFunnelCard';
// Phase 326m — Stuck User Triage panel (diagnose + force-reset credentials)
import StuckUserTriagePanel from '@/components/super-admin/StuckUserTriagePanel';
// Phase 326o — Platform Email Health (Resend key status + 24h send stats + test send)
import PlatformEmailHealthPanel from '@/components/super-admin/PlatformEmailHealthPanel';
// Phase 322b — Live Conversion Funnel (prospect-to-tenant micro-conversions)
import FunnelTelemetryPanel from '@/components/super-admin/FunnelTelemetryPanel';

const COLORS = ['#0f766e', '#14b8a6', '#334155', '#f59e0b', '#f43f5e', '#6366f1'];
const statusColors = {
  pending: 'bg-amber-100 text-amber-700', paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-red-100 text-red-700', lead: 'bg-blue-100 text-blue-700'
};

export default function SuperAdmin() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/super-admin/dashboard').then(r => setDashboard(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
          <Building2 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
          <h2 className="text-lg font-display font-semibold mb-2">Access Denied</h2>
          <p className="text-sm text-muted-foreground">Super Admin access required.</p>
        </CardContent></Card>
      </div>
    );
  }

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-3 gap-4">{[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  const d = dashboard || {};
  const acads = d.academies || {};
  const rev = d.revenue || {};
  const leads = d.leads || {};
  const overdue = d.overdue || {};

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      <div>
        <h1 className="text-2xl font-display font-semibold">Platform Dashboard</h1>
        <p className="text-sm text-muted-foreground">Enterprise platform overview</p>
      </div>

      {/* Progress to 2000 */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progress to 2,000 Academies</span>
            <span className="text-sm font-display font-semibold tabular-nums">{acads.total || 0}/2,000 ({acads.progress || 0}%)</span>
          </div>
          <Progress value={acads.progress || 0} className="h-2" />
        </CardContent>
      </Card>

      {/* Phase 270b — Always-on Onboarding Funnel Signal */}
      <OnboardingFunnelTile />

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Academies</span><Building2 className="h-4 w-4 text-primary" /></div><p className="text-2xl font-display font-semibold tabular-nums">{acads.total || 0}</p></CardContent>
        </Card>
        <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">MRR</span><TrendingUp className="h-4 w-4 text-emerald-600" /></div><p className="text-2xl font-display font-semibold tabular-nums">₹{((rev.mrr || 0) / 1000).toFixed(1)}K</p></CardContent>
        </Card>
        <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Students</span><Users className="h-4 w-4 text-blue-600" /></div><p className="text-2xl font-display font-semibold tabular-nums">{(d.students || 0).toLocaleString()}</p></CardContent>
        </Card>
        <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Pipeline</span><Target className="h-4 w-4 text-purple-600" /></div><p className="text-2xl font-display font-semibold tabular-nums">{leads.total || 0}</p></CardContent>
        </Card>
        <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Overdue</span><AlertTriangle className="h-4 w-4 text-red-500" /></div><p className="text-2xl font-display font-semibold tabular-nums text-red-600">{overdue.count || 0}</p><p className="text-xs text-muted-foreground">₹{((overdue.amount || 0) / 1000).toFixed(1)}K</p></CardContent>
        </Card>
        <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Trials Ending</span><Sparkles className="h-4 w-4 text-amber-500" /></div><p className="text-2xl font-display font-semibold tabular-nums">{d.trials_ending_soon || 0}</p></CardContent>
        </Card>
        {/* Phase 297 — Reputation Engine headline metric (Google Reviews driven) */}
        <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="sa-reviews-driven-tile">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Reviews Driven</span>
              <Star className="h-4 w-4 text-amber-500 fill-amber-400" />
            </div>
            <p className="text-2xl font-display font-semibold tabular-nums text-amber-700" data-testid="sa-reviews-driven-total">
              {(d.google_reviews_driven?.total || 0).toLocaleString()}
            </p>
            <p className="text-[11px] text-muted-foreground">
              <span data-testid="sa-reviews-driven-30d">+{d.google_reviews_driven?.last_30_days || 0}</span> in 30d ·
              <span data-testid="sa-reviews-driven-rate"> {d.google_reviews_driven?.conversion_rate || 0}%</span> conv
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts + Activity */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Academy Distribution</CardTitle></CardHeader>
          <CardContent>
            {(d.city_distribution || []).length > 0 ? (
              <div className="space-y-2">
                {(d.city_distribution || []).map((city, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} /><span className="text-sm">{city.city}</span></div>
                    <span className="text-sm font-semibold tabular-nums">{city.count}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground text-center py-8">No data yet</p>}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Recent Activity</CardTitle></CardHeader>
          <CardContent>
            {(d.recent_activity || []).length > 0 ? (
              <div className="space-y-3">
                {(d.recent_activity || []).slice(0, 8).map((item, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${item.type === 'invoice' ? 'bg-amber-100' : 'bg-blue-100'}`}>
                      {item.type === 'invoice' ? <Receipt className="h-3.5 w-3.5 text-amber-600" /> : <Users className="h-3.5 w-3.5 text-blue-600" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{item.message}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColors[item.status] || 'bg-slate-100 text-slate-600'}`}>{item.status}</span>
                        <span className="text-xs text-muted-foreground">{new Date(item.date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground text-center py-8">No recent activity</p>}
          </CardContent>
        </Card>
      </div>

      {/* Phase 124E — Demo Screenshot Capture (Wall of Champions PNG export) */}
      <DemoCaptureCard />

      {/* Phase 326l — Activation Funnel: % fully set up, top missed milestones, stuck academies */}
      <ActivationFunnelCard />

      {/* Phase 326m — Stuck User Triage: diagnose + force-reset credentials */}
      <StuckUserTriagePanel />

      {/* Phase 326o — Platform Email Health: Resend key + 24h send stats + test send */}
      <PlatformEmailHealthPanel />

      {/* Phase 195.e — Onboarding Media & CRM Blast Engine launcher */}
      <Link to="/super-admin/walkthrough-blast" data-testid="super-admin-walkthrough-blast-link" className="block">
        <Card className="hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-500/10 transition-all cursor-pointer">
          <CardContent className="p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #059669, #10b981)' }}>
                <Send className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold flex items-center gap-2">Onboarding Media &amp; CRM Blast
                  <span className="text-[9px] tracking-wider px-1.5 py-0.5 rounded bg-emerald-600 text-white font-bold">NEW</span>
                </div>
                <div className="text-xs text-muted-foreground">Curate walkthrough videos and broadcast personalised WhatsApp links to partner academies and inbound CRM leads.</div>
              </div>
            </div>
            <Sparkles className="h-4 w-4 text-emerald-600" />
          </CardContent>
        </Card>
      </Link>

      {/* Phase 267 — Super Admin CRM Directory + per-row Outreach Console launcher */}
      <Link to="/super-admin/crm-directory" data-testid="super-admin-crm-directory-link" className="block">
        <Card className="hover:border-indigo-300 hover:shadow-lg hover:shadow-indigo-500/10 transition-all cursor-pointer">
          <CardContent className="p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #818cf8)' }}>
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold flex items-center gap-2">CRM Directory &amp; Per-row Outreach
                  <span className="text-[9px] tracking-wider px-1.5 py-0.5 rounded bg-indigo-600 text-white font-bold">NEW</span>
                </div>
                <div className="text-xs text-muted-foreground">Browse every Academy, Student, and Employee across all tenants. Fire one-tap WhatsApp or Email outreach with built-in 24h cooldown protection.</div>
              </div>
            </div>
            <Sparkles className="h-4 w-4 text-indigo-600" />
          </CardContent>
        </Card>
      </Link>

      {/* Phase 130B — StreamX waitlist demand dashboard launcher */}
      <Link to="/super-admin/livex-waitlist" data-testid="super-admin-livex-link" className="block">
        <Card className="hover:border-red-300 hover:shadow-lg hover:shadow-red-500/10 transition-all cursor-pointer">
          <CardContent className="p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
                <Radio className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold flex items-center gap-2">StreamX Waitlist
                  <span className="text-[9px] tracking-wider px-1.5 py-0.5 rounded bg-rose-500 text-white font-bold">SOON</span>
                </div>
                <div className="text-xs text-muted-foreground">Demand signals from academies interested in the StreamX broadcasting product.</div>
              </div>
            </div>
            <Sparkles className="h-4 w-4 text-red-600" />
          </CardContent>
        </Card>
      </Link>

      {/* Phase 131 — StreamX Alpha Studio launcher (super_admin can demo end-to-end) */}
      <Link to="/streamx/studio" data-testid="super-admin-livex-studio-link" className="block">
        <Card className="hover:border-rose-400 hover:shadow-lg hover:shadow-rose-500/20 transition-all cursor-pointer border-2 border-rose-200">
          <CardContent className="p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
                <Radio className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold flex items-center gap-2">StreamX Studio
                  <span className="text-[9px] tracking-wider px-1.5 py-0.5 rounded bg-rose-600 text-white font-bold animate-pulse">ALPHA</span>
                </div>
                <div className="text-xs text-muted-foreground">Demo a live broadcast end-to-end. Mint a stream + share the public watch link.</div>
              </div>
            </div>
            <Sparkles className="h-4 w-4 text-rose-600" />
          </CardContent>
        </Card>
      </Link>

      {/* Phase 255-DemandTracker — AI Add-ons demand signal tile */}
      <AddonsDemandTile />

      {/* Phase 270 — Adoption-Nudge Console */}
      <AdoptionNudgeConsole />

      {/* Phase 89E.c — Cross-tenant Webhook Health Dashboard */}
      <WebhookHealthTile />

      {/* Phase 286.b — Cross-tenant Notification Audit Dashboard */}
      <NotificationAuditTile />
    </div>
  );
}
